import { useEffect, useState, createContext, useContext } from "react";
import api from "../lib/api";
import { Megaphone, Sparkles } from "lucide-react";

/**
 * Site-wide promotion context. Fetches the single active promo from
 * `GET /api/promotions/active` once on mount and shares it with any
 * surface that wants to render a banner (landing, summary, upsell screen).
 */
const PromotionContext = createContext({ promotion: null, loading: true });

export function PromotionProvider({ children }) {
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/promotions/active")
      .then((r) => { if (!cancelled) setPromotion(r.data || null); })
      .catch(() => { /* no banner on error — graceful */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <PromotionContext.Provider value={{ promotion, loading }}>
      {children}
    </PromotionContext.Provider>
  );
}

export function useActivePromotion() {
  return useContext(PromotionContext);
}

const defaultBannerText = (p) => {
  if (!p) return "";
  if (p.banner_text) return p.banner_text;
  if (p.type === "free_upsell") {
    const labels = {
      delivery: "Free Delivery",
      delivery_install: "Free Delivery + Install",
      pro_measurement: "Free Pro Measurement",
      white_glove: "Free White Glove Install",
    };
    const parts = (p.free_upsell_keys || []).map((k) => labels[k] || k);
    return `${parts.join(" & ")} on orders over $${p.threshold_amount}!`;
  }
  return `Save an extra ${p.percent}% on orders over $${p.threshold_amount}!`;
};

/**
 * Hero banner: site-wide marketing strip. Use at the top of the landing
 * page + funnel root. Hidden if no active promo. Compact + dismissible
 * is intentional — we want it loud, not annoying.
 */
export function PromoHeroBanner({ className = "" }) {
  const { promotion } = useActivePromotion();
  if (!promotion) return null;
  return (
    <div
      data-testid="promo-hero-banner"
      className={`text-white text-center py-2 px-4 text-sm font-semibold ${className}`}
      style={{ background: "linear-gradient(90deg, #166534, #16a34a)" }}
    >
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Megaphone className="w-4 h-4 inline-block" />
        <span>{defaultBannerText(promotion)}</span>
      </div>
    </div>
  );
}

/**
 * Step-Summary contextual card. Shows progress to threshold ("Add $42
 * more to unlock free delivery") OR "Unlocked!" once they've crossed it.
 * Reads the live `quote.promotion` block returned by /api/quote so the
 * progress updates as the customer adjusts quantity.
 */
export function PromoProgressCard({ quote }) {
  const { promotion } = useActivePromotion();
  if (!promotion || !quote) return null;
  const p = quote.promotion;
  if (!p) return null;
  const unlocked = p.threshold_met;
  const savings = p.savings || 0;
  return (
    <div
      data-testid="promo-progress-card"
      className="rounded-2xl px-4 py-3 mb-3 border-2"
      style={{
        background: unlocked ? "#ecfdf5" : "#fffbeb",
        borderColor: unlocked ? "#6ee7b7" : "#fcd34d",
      }}
    >
      <div className="flex items-center gap-2">
        {unlocked ? <Sparkles className="w-4 h-4 brand-text" /> : <Megaphone className="w-4 h-4" style={{ color: "#b45309" }} />}
        <p className={`text-sm font-semibold ${unlocked ? "brand-text" : ""}`} style={!unlocked ? { color: "#92400e" } : {}}>
          {unlocked
            ? (savings > 0
                ? `Promo unlocked — saving $${savings.toFixed(2)}!`
                : `Promo unlocked! ${defaultBannerText(promotion)}`)
            : `Add $${p.amount_to_unlock.toFixed(2)} more to unlock ${defaultBannerText(promotion).replace(/!$/, "")}`}
        </p>
      </div>
    </div>
  );
}

/**
 * "FREE — promo applied" badge for use on the post-purchase upsell tiles.
 * Renders only when (a) a free_upsell promo is active, (b) the customer
 * has crossed the threshold, and (c) the upsell tile being rendered is
 * one of the unlocked keys.
 */
export function PromoUpsellBadge({ upsellKey, currentSubtotal }) {
  const { promotion } = useActivePromotion();
  if (!promotion || promotion.type !== "free_upsell") return null;
  if (!(promotion.free_upsell_keys || []).includes(upsellKey)) return null;
  const met = (currentSubtotal || 0) >= (promotion.threshold_amount || 0);
  if (!met) return null;
  return (
    <span
      data-testid={`promo-upsell-badge-${upsellKey}`}
      className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ml-1"
      style={{ background: "#166534", color: "#fff" }}
    >
      Free with promo
    </span>
  );
}
